import { Switch, Tooltip } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomSelect from 'src/components/CustomSelect';
import { FormItem } from 'src/components/SharedDetailsComponents';
import { useGlobal } from 'src/contexts/GlobalContext';
import CardLabel from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/CardTitle';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';

import DeleteSite from '../../components/DeleteSite';
import { useSiteDetails } from '../../SiteDetailsContext';
import { ContentWrapper, StyledDivider, StyledInput } from './Settings.styled';

export const Settings = () => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { formMethods, authenticationConnections, site } = useSiteDetails();

  const authenticationConnectionsOptions = authenticationConnections
    ?.filter(
      (connection) =>
        connection.connectionType !== 'OrderFulfillment' &&
        connection.connectionTypeDetail !== 'OrderFulfillmentFortressAiFi',
    )
    ?.map((connection) => ({
      label: connection.name ?? '',
      value: connection.id ?? '',
    }));

  const enabledCampaigns = site?.campaigns?.filter((campaign) => campaign.enabled);
  const connections = enabledCampaigns?.map((campaign) =>
    authenticationConnections?.find((connection) => connection.id === campaign.connectionId),
  );
  const isFortressAiFiCampaignEnabled = connections?.some(
    (connection) =>
      connection?.connectionType === 'OrderFulfillment' &&
      connection?.connectionTypeDetail === 'OrderFulfillmentFortressAiFi',
  );

  return (
    <>
      <SettingCard
        title={t('site.general.title')}
        description={t('site.general.description')}
        data-testid="site-settings-card"
        items={[
          {
            content: (
              <ContentWrapper $isDesktop={isDesktop} data-testid="site-settings-form">
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.general.sitePath')}
                  name="pathSegment"
                  help={formMethods?.formState?.errors?.pathSegment?.message}
                  validateStatus={formMethods?.formState?.errors?.pathSegment ? 'error' : ''}
                >
                  <Controller
                    name="pathSegment"
                    control={formMethods.control}
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        prefix="/"
                        name="pathSegment"
                        placeholder={t('site.general.sitePathPlaceholder')}
                        data-testid="site-path-input"
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.general.authenticationConnection')}
                  name="authenticationConnectionId"
                >
                  <Controller
                    name="authenticationConnectionId"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Tooltip
                        title={
                          isFortressAiFiCampaignEnabled
                            ? t('site.general.authenticationConnectionDisabledTooltip')
                            : undefined
                        }
                      >
                        <span>
                          <CustomSelect
                            field={field}
                            placeholder={t('site.general.authenticationConnectionPlaceholder')}
                            options={authenticationConnectionsOptions ?? []}
                            data-testid="auth-connection-select"
                            onOk={field.onChange}
                            title={t('site.general.authenticationConnection')}
                            onOkText={t('site.general.saveAuthenticationConnection')}
                            disabled={isFortressAiFiCampaignEnabled}
                          />
                        </span>
                      </Tooltip>
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={isDesktop}
                  label={
                    <CardLabel
                      title={t('site.general.personNamesLabel')}
                      tooltipTitle={t('site.general.personNamesLabelTooltip')}
                    />
                  }
                  name="requirePersonNames"
                >
                  <Controller
                    name="requirePersonNames"
                    control={formMethods.control}
                    render={({ field }) => <Switch {...field} data-testid="person-names-switch" />}
                  />
                </FormItem>
              </ContentWrapper>
            ),
          },
        ]}
      />
      {isDesktop && <StyledDivider />}
      <DeleteSite />
    </>
  );
};
